import React, { useState, useEffect } from 'react';
import { Edit3, Save, X, FileText, AlertCircle, CheckCircle, Hash } from 'lucide-react';
import folioService from '../../services/folioService';
import '../../pages/Configuracion.css';

const FolioManagement = () => {
  const [folioConfig, setFolioConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Load folio configuration on component mount
  useEffect(() => {
    loadFolioConfig();
  }, []);

  const loadFolioConfig = async () => {
    try {
      setLoading(true);
      setError(null);
      const config = await folioService.getFolioConfig();
      setFolioConfig(config);
    } catch (error) {
      console.error('Error loading folio config:', error);
      setError('Error al cargar la configuración de folios: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setEditValue(folioConfig?.ultimo_folio?.toString() || '0');
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue('');
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    try {
      // Validate input
      const folioNumber = parseInt(editValue);
      if (isNaN(folioNumber) || folioNumber < 0) {
        setError('El folio debe ser un número entero positivo o cero');
        return;
      }

      setSaving(true);
      setError(null);
      
      const updatedConfig = await folioService.updateFolioConfig(folioNumber);
      setFolioConfig(updatedConfig);
      setIsEditing(false);
      setEditValue('');
      setSuccess('Configuración de folio actualizada correctamente');
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving folio config:', error);
      setError('Error al guardar la configuración: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    // Only allow numeric input
    if (value === '' || /^\d+$/.test(value)) {
      setEditValue(value);
      setError(null);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  return (
    <div className="configuracion-container">
      <div className="configuracion-header">
        <h1 className="configuracion-title">Configuración - Folios</h1>
        <p className="configuracion-subtitle">
          Gestiona los folios secuenciales para instructivos de embarque
        </p>
      </div>

      <div className="configuracion-content">
        {/* Sección Gestión de Folios */}
        <section className="config-section">
          <div className="config-section-header">
            <Hash size={24} className="config-section-icon" />
            <h2 className="config-section-title">Gestión de Folios</h2>
          </div>

          <div className="config-section-content">
            <div className="config-info-card">
              {loading ? (
                <p className="config-info-loading">Cargando configuración...</p>
              ) : error ? (
                <div className="alert alert-error">
                  <AlertCircle size={20} />
                  <span>{error}</span>
                </div>
              ) : (
                <>
                  {success && (
                    <div className="alert alert-success" style={{ marginBottom: '1rem' }}>
                      <CheckCircle size={20} />
                      <span>{success}</span>
                    </div>
                  )}

                  <div className="config-info-item">
                    <FileText size={20} className="config-info-icon" />
                    <div className="config-info-details">
                      <span className="config-info-label">Último Folio Entregado:</span>
                      {isEditing ? (
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginTop: '0.5rem' }}>
                          <input
                            type="text"
                            value={editValue}
                            onChange={handleInputChange}
                            onKeyDown={handleKeyPress}
                            className="config-input"
                            placeholder="Ingrese el número de folio"
                            autoFocus
                            disabled={saving}
                            style={{ 
                              padding: '0.5rem',
                              border: '1px solid #d1d5db',
                              borderRadius: '4px',
                              fontSize: '1rem',
                              width: '150px'
                            }}
                          />
                          <button
                            onClick={handleSave}
                            disabled={saving}
                            className="btn-config btn-config-primary"
                            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                          >
                            {saving ? (
                              <>Guardando...</>
                            ) : (
                              <>
                                <Save size={14} />
                                Guardar
                              </>
                            )}
                          </button>
                          <button
                            onClick={handleCancel}
                            disabled={saving}
                            className="btn-config btn-config-secondary"
                            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                          >
                            <X size={14} />
                            Cancelar
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem' }}>
                          <span className="config-info-value" style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: '700',
                            color: '#2c5f2d'
                          }}>
                            {folioConfig?.ultimo_folio || 0}
                          </span>
                          <button
                            onClick={handleEdit}
                            className="btn-config btn-config-outline"
                            style={{ padding: '0.5rem 1rem', fontSize: '0.875rem' }}
                          >
                            <Edit3 size={14} />
                            Editar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  {folioConfig && !isEditing && (
                    <>
                      <div className="config-info-item">
                        <Hash size={20} className="config-info-icon" />
                        <div className="config-info-details">
                          <span className="config-info-label">Próximo folio a asignar:</span>
                          <span className="config-info-value config-info-badge">
                            {(folioConfig.ultimo_folio || 0) + 1}
                          </span>
                        </div>
                      </div>
                      <div className="config-info-item">
                        <CheckCircle size={20} className="config-info-icon" />
                        <div className="config-info-details">
                          <span className="config-info-label">Última actualización:</span>
                          <span className="config-info-value">
                            {new Date(folioConfig.updated_at).toLocaleString('es-CL')}
                          </span>
                        </div>
                      </div>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className="configuracion-footer">
        <p className="configuracion-footer-text">
          El sistema asigna automáticamente folios secuenciales al generar instructivos de embarque. 
          Cada preparativo recibe un folio único que se mantiene en regeneraciones.
        </p>
      </div>
    </div>
  );
};

export default FolioManagement;
